/**
 * SidebarV2 — F 194 Item 1
 *
 * ONE sidebar that carries BOTH halves of the old duplicate pair:
 *
 *   - the page tree (nesting, expand/collapse, drag, rename, context menu,
 *     page creation) — taken by MOUNTING the existing PageTree component.
 *     Its 891 lines are NOT copied, re-implemented or forked here. This file
 *     imports it and renders it.
 *   - the task navigation that the old Sidebar.tsx could draw but never did
 *     (its `taskNav` prop had no caller, so the group rendered nothing).
 *     Here the counts are LIVE: read from TaskStore, and refreshed on
 *     task:* events.
 *
 * PageTree.tsx is preserved. Sidebar.tsx is preserved and still exported from
 * the barrels. This component sits on top of both; it replaces neither.
 * F 191 recommended sequencing the merge ahead of the cut-over, and this is
 * the build step of that sequence.
 *
 * Why the task nav is markup here rather than `<Sidebar items=... />`:
 * Sidebar renders a "Surfaces" group that is always present, and today no
 * surface list is real — every consumer of it is a test. Mounting Sidebar
 * wholesale would ship an empty heading plus five dead buttons. The 5 nav
 * rows below reuse Sidebar's exported `TaskNavItem` type and its CSS class
 * names, so they are visually identical to what Sidebar would have drawn.
 * The expensive half — the page tree — is reused wholesale, not retyped.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack } from '@mantine/core';
import { PageTree, openQuickCapture } from './PageTree';
import type { TaskNavItem } from './Sidebar';
import type { Task, TaskViewStatus } from '../../../types/models/Task';
import { TASK_VIEW_STATUSES, normalizeTaskStatus } from '../../../types/models/Task';
import { TaskStore } from '../../../core/task/store';
import { useWorkspace } from '../../../core/workspace/WorkspaceContext';
import type { ViewLayout } from '../../../core/views/viewConfig';
import { loadDatabase, loadDatabases, saveDatabase } from '../../../core/workspace/databaseStorage';
import { RemoteState } from '../../../core/storage/RemoteState';
import { eventBus } from '../../../core/event-bus';
import { NewDatabaseModal } from './NewDatabaseModal';
import { SIDEBAR_CREATE_EVENT, type SidebarCreateKind } from './sidebarCreateEvent';
import { useSidebarSectionState, type SidebarSectionId } from './sidebarSectionState';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import './Sidebar.css';

/**
 * F 472 — THE SECTION ACTION.
 *
 * The icon button that sits on a section's heading row, BEFORE its list.
 *
 * Why it moved: the only "add" affordance the sidebar had was `+ New database`
 * — a LIST ROW at the bottom of the databases list. Measured on production
 * (build `2c50b10`): seven databases put it at y=546, and the sidebar's
 * content is 800+px tall against a 772px viewport, so TRASH was already below
 * the fold. An affordance that can scroll out of reach is not an affordance.
 *
 * Why it is an ICON with a hidden label: a label on every heading row is
 * noise, and the operator asked for "1 + icon". The text still exists in the
 * DOM (visually hidden) because an icon-only button needs an accessible name —
 * and because the shipped specs look for it by text.
 *
 * NOTE — the HEADING stays inline in this file, not in a wrapper component.
 * The F 229 CSS-contract spec reads this file's source to prove that all four
 * groups carry a heading of the same class and that Trash's heading sits
 * inside its own group. A shared wrapper would have satisfied the intent and
 * broken that check; the heading text differs per group anyway, so it was
 * never going to be DRY.
 */
const SectionAction: React.FC<{
  /** Accessible name of the action. Also the visually hidden label. */
  label: string;
  testId?: string;
  onAction: () => void;
}> = ({ label, testId, onAction }) => (
  <button
    type="button"
    className="sidebar-group__action"
    onClick={onAction}
    title={label}
    aria-label={label}
    data-testid={testId}
  >
    <span aria-hidden="true">+</span>
    <span className="fc-sr-only">{label}</span>
  </button>
);

/**
 * F 498 — THE FOLD TOGGLE.
 *
 * The button that folds a section, and the chevron that shows its state.
 *
 * WHY THE CHEVRON IS A SIBLING OF THE HEADING, NOT INSIDE IT
 * ----------------------------------------------------------
 * The heading element and its class are load-bearing for three separate
 * things that read this file:
 *
 *   - F 229's CSS-contract spec greps this file's SOURCE for the heading
 *     element with its class, and asserts the labels it finds are exactly
 *     the four group names, in order;
 *   - F 196A's spec collects every heading in the rendered DOM and compares
 *     its text, so a chevron INSIDE the heading would change that text;
 *   - the heading's CSS is the small-caps muted treatment, and an icon
 *     inside it would inherit that treatment.
 *
 * So the chevron is a sibling, and the heading's text is untouched.
 *
 * WHY THE HEADING IS THE BUTTON
 * ------------------------------
 * The dispatch says "clicking a header toggles that section". The heading is
 * the header. Making the chevron the only click target would mean a 12px
 * target, which is below the 32px row the rest of the sidebar uses, and it
 * would make the heading look like a label rather than a control.
 *
 * So the HEADING is the button, and the chevron is inside it, at the LEFT,
 * next to the heading text. The heading is then the toggle, the chevron is
 * the state indicator, and the "+" is untouched — the F 472 contract that
 * the create action lives on the heading row, BEFORE the list, still holds.
 *
 * The "+" is a SEPARATE button outside this one. Two buttons cannot be
 * nested, and a nested button is invalid HTML that browsers silently break
 * apart.
 *
 * WHY THE HEADING IS PASSED IN AS CHILDREN, NOT RENDERED HERE
 * -----------------------------------------------------------
 * F 229's spec matches this file's SOURCE TEXT for the heading element with
 * a LITERAL label in it, and reads the labels out of the matches. If this
 * component rendered the heading itself from a prop, the source would carry
 * a placeholder instead of a label, the regex would match that placeholder
 * INSTEAD of the real headings, and the spec would report the wrong labels
 * and go RED.
 *
 * That is not hypothetical: it is exactly what the first version of this
 * component did, and the F 229 spec caught it. The heading is therefore
 * passed in as CHILDREN, so each call site carries its own real heading in
 * the source, and the spec keeps seeing four real headings.
 *
 * THE SAME TRAP, IN THE PROSE
 * ---------------------------
 * The spec's pattern matches ANYWHERE in the file — INCLUDING INSIDE A
 * COMMENT. An earlier draft of this comment spelled out the heading element
 * literally, and the spec matched the COMMENT, reported five labels instead
 * of four, and failed. So this comment deliberately describes the markup
 * without reproducing it. Anyone editing this file must keep it that way: a
 * comment that spells the heading out will break F 229.
 *
 * This is the same constraint the original file recorded for the heading
 * itself: the heading stays inline in this file, not in a wrapper
 * component, because the F 229 CSS-contract spec reads this file's source
 * to prove that all four groups carry a heading of the same class.
 */
const SectionToggle: React.FC<{
  /** The section's id — the key it is stored under. */
  section: SidebarSectionId;
  /** Is the section open right now? */
  open: boolean;
  /** Fold or unfold it. */
  onToggle: () => void;
  /** The heading, passed in so this file's source keeps the literal label. */
  children: React.ReactNode;
}> = ({ section, open, onToggle, children }) => (
  <button
    type="button"
    className="sidebar-group__toggle"
    onClick={onToggle}
    aria-expanded={open}
    aria-controls={`fc-sidebar-v2-${section}-content`}
    data-testid={`fc-sidebar-v2-toggle-${section}`}
    data-open={open}
  >
    {/* F 498 — THE CHEVRON. The same Tabler icons and the same 12px size
        PageTree uses for its own expand/collapse chevron, so the sidebar
        speaks the same visual language as the tree inside it. */}
    <span
      className="sidebar-group__chevron"
      aria-hidden="true"
      data-testid={`fc-sidebar-v2-chevron-${section}`}
    >
      {open ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
    </span>
    {children}
  </button>
);

// F 472 — the ONE event the global "+" menu speaks lives in
// `sidebarCreateEvent.ts`, so the menu component does not have to import this
// whole file to agree with it on the name.


/** Per-view presentation. Ids match `TaskNavItem['id']` and `TaskViewStatus`. */
const VIEW_META: Record<TaskViewStatus, { label: string; icon: string }> = {
  inbox: { label: 'Inbox', icon: '\u{1F4E5}' },
  today: { label: 'Today', icon: '\u{1F4C5}' },
  upcoming: { label: 'Upcoming', icon: '\u{1F6EB}' },
  anytime: { label: 'Anytime', icon: '\u{1F33F}' },
  done: { label: 'Done', icon: '\u2713' },
};

export interface SidebarV2Props {
  /** Phase number, forwarded to PageTree. */
  phase?: number;
  /** Currently selected page, forwarded to PageTree. */
  currentPageId?: string;
  /** Page selection handler, forwarded to PageTree. */
  // F 320 — `kind` is optional and is passed only by the database rows below
  // (line 236), which are the only place in the tree that knows `p.type`.
  // PageTree rows pass a single argument, so they keep page routing.
  onPageSelect?: (pageId: string, kind?: string) => void;
  /** Task view the operator clicked, or null when a page is in focus. */
  activeTaskView?: TaskViewStatus | null;
  /**
   * Called with the clicked task view. The mount point decides what to render
   * for it; SidebarV2 does not own the main content area.
   */
  onTaskNavigate?: (view: TaskViewStatus) => void;
  /**
   * TaskStore override. Production omits this and gets the default store;
   * tests inject a fake so they do not need an indexeddb adapter.
   */
  store?: TaskStore;
  /**
   * F 226 — TRASH. True while the Trash view is open. SidebarV2 does not
   * own the main content area, so it only reports the click; the mount
   * point renders <TrashView />.
   */
  trashOpen?: boolean;
  onTrashToggle?: () => void;
}

export const SidebarV2: React.FC<SidebarV2Props> = ({
  phase = 7,
  currentPageId,
  onPageSelect,
  activeTaskView = null,
  onTaskNavigate,
  store,
  trashOpen = false,
  onTrashToggle,
}) => {
  // F 498 — THE FOLD STATE. Read on the FIRST render, not in an effect, so
  // a reload does not paint the sidebar open and then snap it shut.
  const { isOpen: isSectionOpen, toggle: toggleSection } = useSidebarSectionState();

  const internalStore = useMemo(() => store ?? new TaskStore(), [store]);
  const [tasks, setTasks] = useState<Task[]>([]);

  // F 196A — Databases section. `useWorkspace()` may be absent (the sidebar
  // is unit-tested without a provider), so this stays optional.
  const ws = (() => {
    try { return useWorkspace(); } catch { return undefined; }
  })();
  // F 457 — WHY THIS LIST WAS ALWAYS EMPTY.
  //
  // It used to be `(ws?.pages ?? []).filter((p) => p.type === 'database')`.
  // Measured on the live graph: `freshcards:pages` holds exactly 3 records
  // (page-prd, page-getting-started, page-daily-notes) and ALL THREE have
  // `type === 'page'`. Not one has `type === 'database'`, so the filter
  // matched nothing and the Databases section rendered an empty nav — which is
  // why Quick Notes and Working Canvas never appeared.
  //
  // The databases are NOT pages. They live in their own key,
  // `freshcards:databases`, as a map keyed by database id. Measured contents:
  // 7 entries — Test DB (3 items), Roadmap Q1 (0), Quick Notes (641),
  // Working Canvas (0), Extraction Audit (1), Working Canvas (1),
  // HQ Backup (0).
  //
  // So read the same key DatabaseView already reads. `loadDatabases()` is the
  // one accessor for it and is what `DatabaseView` uses to open a database, so
  // reusing it keeps the sidebar and the view on a single source of truth —
  // and keeps F 453's `/database/:id` route working, since that route resolves
  // through the same storage module.
  //
  // `dbTick` is bumped by an effect rather than read from `ws.pages`, because
  // the graph store is hydrated asynchronously and exposes no subscription: a
  // memo keyed on `ws.pages` would freeze at mount and never see the databases
  // arrive. This is the F 443 stale-memo defect, not a new one.
  const [dbTick, setDbTick] = useState(0);
  const dbList = useMemo(() => {
    // F 471 — ONE LIST OF DATABASES. There is no special case left.
    //
    // F 457 skipped HQ Backup here because it was a FAKE database: its 16,783
    // records lived in `freshcards:hq_item:*` and the record itself said
    // `items: 0`, so listing it here would have given the operator a second
    // row reading "0 items" beside the real one. F 470 moved the items INSIDE
    // the record, so the record can count them — and the special case is gone.
    //
    // The count rule (F 470) is what makes this correct: an inline database
    // counts its items, a PAGED one counts what its record says it owns. That
    // is the only difference between HQ Backup and any other row.
    const map = loadDatabases();
    return Object.entries(map)
      .map(([id, db]) => ({
        id,
        name: db.name || 'Untitled Database',
        // F 470 — THE COUNT RULE. An inline database counts its items; a
        // PAGED one counts what its record says it owns.
        count: Array.isArray(db.items) && db.items.length > 0
          ? db.items.length
          : (db.itemPages?.total ?? 0),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    // `dbTick` is the dependency that makes this re-read; `loadDatabases()`
    // reads a module-level store and is otherwise invisible to React.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbTick]);

  // Re-read the database map shortly after mount: on a cold load the graph
  // store is still hydrating, so a read on first render can see an empty map
  // and never see the real one.
  useEffect(() => {
    const t = window.setTimeout(() => setDbTick((n) => n + 1), 400);
    return () => window.clearTimeout(t);
  }, []);

  // F 470 — WAIT FOR THE HYDRATION. DO NOT GUESS AT IT.
  //
  // The 400 ms timer above is a GUESS. The F 463 measurement is what it
  // guesses about: a cold load hydrates 20.9 MB and the data lands at
  // +4866 ms, so a 400 ms re-read fires long before the store has
  // anything and the list stayed EMPTY FOREVER. A warm cache was the only
  // reason it ever looked right, which is why the bug survived a build.
  //
  // So the sidebar also waits for the store's OWN completion signal.
  // `RemoteState.isHydrated()` is NOT that signal — it is set at the TOP
  // of `init()` as a re-entrancy guard, so it means "an init is in
  // flight", not "the data has arrived". `whenHydrated()` resolves when
  // the hydration has actually finished.
  //
  // The 400 ms re-read STAYS: it is what makes the list appear when the
  // store was never initialised at all (a test harness, a store that is
  // already warm), and it costs one read.
  useEffect(() => {
    let cancelled = false;
    void RemoteState.whenHydrated().then(() => {
      if (!cancelled) setDbTick((n) => n + 1);
    });
    return () => { cancelled = true; };
  }, []);
  const [newDbOpen, setNewDbOpen] = useState(false);

  /**
   * F 472 — THE THREE CREATE ACTIONS, IN ONE PLACE.
   *
   * The global "+" (in the Workspace header) and each section's own "+" both
   * land here, so there is exactly one implementation of "add a page", "add a
   * database" and "add a task" and the two entrances cannot drift.
   */
  const handleCreateTask = useCallback(() => {
    // `openQuickCapture` is the ONE dispatcher of `fc:quick-capture:toggle` and
    // it lives in PageTree.tsx (F 78). Calling it rather than emitting a
    // second event keeps "one source of truth" literally true: there is still
    // one dispatch site in the codebase.
    openQuickCapture();
  }, []);

  const handleCreateDatabase = useCallback(() => {
    setNewDbOpen(true);
  }, []);

  /**
   * F 472 — the page tree publishes its create action UPWARD.
   *
   * The tree's create is not a one-liner: it creates the page, selects it,
   * expands the parent, moves the tree cursor and OPENS THE INLINE RENAME
   * editor. Re-implementing that here would be a second implementation of
   * page creation — the same mistake F 471 removed when it deleted the second
   * database row. So the tree hands the action up and the section's "+" drives
   * it.
   *
   * A REF, not state: the parent's callback writes to the ref and never sets
   * state, so the tree's effect can publish the action without causing a
   * render loop.
   */
  const createPageRef = useRef<(() => void) | null>(null);
  const registerCreatePage = useCallback((action: () => void) => {
    createPageRef.current = action;
  }, []);

  const handleCreatePage = useCallback(() => {
    createPageRef.current?.();
  }, []);

  /**
   * F 472 — the global "+" menu's request, routed to the same three actions.
   *
   * The handler is held in a REF, not a `useCallback`, and that is deliberate.
   * `eventBus.off(event, handler)` removes BY REFERENCE, so a `useCallback`
   * handler — whose identity is shared by every instance that has the same
   * deps — is removed by the FIRST instance that unmounts, silently
   * unsubscribing a second, still-mounted instance. A ref-initialised
   * function is unique per instance, so each one can only ever unsubscribe
   * itself.
   */
  const createRequestHandler = useRef<((data: { kind?: SidebarCreateKind }) => void) | null>(null);
  if (!createRequestHandler.current) {
    createRequestHandler.current = (data) => {
      const kind = data?.kind ?? 'page';
      if (kind === 'database') handleCreateDatabase();
      else if (kind === 'task') handleCreateTask();
      else handleCreatePage();
    };
  }

  useEffect(() => {
    const handler = createRequestHandler.current!;
    eventBus.on(SIDEBAR_CREATE_EVENT, handler);
    return () => { eventBus.off(SIDEBAR_CREATE_EVENT, handler); };
  }, []);

  // Creating a database also creates its page (the page id doubles as the
  // database id — see WorkspaceContext.createPage), so the row appears in
  // this list and in the page tree at the same time.
  const handleCreateDatabaseSubmit = useCallback(
    async (name: string, description: string, layout: ViewLayout) => {
      // `WorkspaceContextValue.createPage` is declared with two params while
      // its implementation takes a third `type` — a pre-existing gap in the
      // shared interface, not something F 196A should widen from a sidebar.
      // Cast locally to the implementation's real shape.
      const createTyped = ws?.createPage as
        | ((t: string, p?: string, type?: 'page' | 'database') => Promise<{ id: string }>)
        | undefined;
      const page = await createTyped?.(name || 'Untitled Database', undefined, 'database');
      if (!page) return;

      // The chosen layout belongs on the DATABASE, not the page. A Database
      // owns `views: DatabaseViewConfig[]`, and `createDefaultDatabase` seeds
      // a Board + a List view. Put the operator's choice FIRST in that list so
      // it is the view the surface opens on, rather than inventing a field.
      // `PageRef.coverLayout` is cover-only ('wide'|'banner'|'none') and is
      // NOT a layout channel — using it here would have been wrong.
      const db = loadDatabase(page.id);
      if (db) {
        const chosen = { id: `${page.id}_${layout}`, name: layout, type: layout };
        const rest = db.views.filter((v) => v.type !== layout);
        // `Database` has no description field (id/name/icon/cover/schema/
        // views/items/createdAt/updatedAt). Adding one would be inventing
        // schema, so the description is not persisted — the field stays in
        // the modal for the operator's benefit and is deliberately dropped.
        void description;
        saveDatabase({ ...db, name, views: [chosen, ...rest] });
        eventBus.emit('database:refresh', { databaseId: page.id });
      }
      setNewDbOpen(false);
    },
    [ws]
  );

  // Load once, then keep in sync via the store's single `task:event` channel.
  // Same shape TasksView uses, so the two views cannot drift.
  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      try {
        const all = await internalStore.list();
        if (alive) setTasks(all);
      } catch {
        // A missing storage adapter (SSR, first paint, test env without a
        // fake store) must not take the sidebar down. Counts stay at 0 and
        // the page tree still works.
      }
    };
    void refresh();
    const unsub = internalStore.subscribe(() => { void refresh(); });
    return () => { alive = false; unsub(); };
  }, [internalStore]);

  // Counts by view. `done` absorbs the legacy 'cancelled' status, matching
  // TasksView's belongsIn().
  const counts = useMemo(() => {
    const out: Record<TaskViewStatus, number> = {
      inbox: 0, today: 0, upcoming: 0, anytime: 0, done: 0,
    };
    for (const t of tasks) {
      const s = normalizeTaskStatus(t.status);
      if (s === 'done') out.done += 1;
      else out[s] += 1;
    }
    return out;
  }, [tasks]);

  // Always five entries, even at zero — that is the difference between this
  // and the inert Sidebar group. `SidebarV2` owns its taskNav rather than
  // waiting for a caller to hand one down.
  const taskNav: TaskNavItem[] = useMemo(
    () => TASK_VIEW_STATUSES.map((id) => ({
      id,
      label: VIEW_META[id].label,
      icon: VIEW_META[id].icon,
      count: counts[id],
    })),
    [counts]
  );

  const handleTaskClick = useCallback((view: TaskViewStatus) => {
    onTaskNavigate?.(view);
  }, [onTaskNavigate]);

  return (
    <Box className="sidebar-v2" data-testid="fc-sidebar-v2" h="100%">
      <Stack gap={0} h="100%">
        {/* ---- Task navigation: the half Sidebar could draw but never did ----
             F 472 — the section's own "+" lives on the HEADING row, before the
             list, so it cannot scroll away. It adds a TASK — which is why the
             task affordance belongs here and not under "Pages", where the
             operator found it. */}
        <div className="sidebar-group" data-testid="fc-sidebar-v2-tasks">
          <div className="sidebar-group__header">
            <SectionToggle
              section="tasks"
              open={isSectionOpen('tasks')}
              onToggle={() => toggleSection('tasks')}
            >
              <h3 className="sidebar-group__heading">Tasks</h3>
            </SectionToggle>
            <SectionAction
              label="New task"
              testId="fc-sidebar-v2-new-task"
              onAction={handleCreateTask}
            />
          </div>
          <nav
            className="sidebar-nav"
            id="fc-sidebar-v2-tasks-content"
            hidden={!isSectionOpen('tasks')}
          >
            {taskNav.map((task) => {
              const isActive = activeTaskView === task.id;
              return (
                <button
                  key={task.id}
                  type="button"
                  className={`sidebar-item ${isActive ? 'active' : ''}`}
                  onClick={() => handleTaskClick(task.id)}
                  title={`Navigate to ${task.label}`}
                  data-testid={`fc-sidebar-v2-task-${task.id}`}
                  data-current={isActive}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <span className="sidebar-item__icon" aria-hidden="true">{task.icon}</span>
                  <span className="sidebar-label">{task.label}</span>
                  {task.count > 0 && (
                    <span className="sidebar-count" data-testid={`fc-sidebar-v2-task-${task.id}-count`}>
                      {task.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* ---- Databases: F 196A. The rows are pages of type 'database';
             ContentRouter already routes that type to DatabaseView, so
             selecting a row opens the real database surface. ----
             F 472 — "+ New database" is no longer a row at the BOTTOM of the
             list. It is the section's "+", on the heading row. The testid is
             unchanged, so the F 196A spec that looks for it still finds it and
             still opens the same modal. */}
        <div className="sidebar-group" data-testid="fc-sidebar-v2-databases">
          <div className="sidebar-group__header">
            <SectionToggle
              section="databases"
              open={isSectionOpen('databases')}
              onToggle={() => toggleSection('databases')}
            >
              <h3 className="sidebar-group__heading">Databases</h3>
            </SectionToggle>
            <SectionAction
              label="New database"
              testId="fc-sidebar-v2-new-database"
              onAction={handleCreateDatabase}
            />
          </div>
          <nav
            className="sidebar-nav"
            id="fc-sidebar-v2-databases-content"
            hidden={!isSectionOpen('databases')}
          >
            {dbList.map((d) => (
              <button
                key={d.id}
                type="button"
                className={`sidebar-item ${currentPageId === d.id ? 'active' : ''}`}
                onClick={() => onPageSelect?.(d.id, 'database')}
                title={`Open database ${d.name}`}
                data-testid={`fc-sidebar-v2-database-${d.id}`}
                data-count={d.count}
                data-current={currentPageId === d.id}
              >
                <span className="sidebar-item__icon" aria-hidden="true">📊</span>
                <span className="sidebar-label">{d.name}</span>
                {/* F 457 — name and item count, and nothing else. The operator
                    asked for no raw records in the nav; the items belong to the
                    view this row opens. */}
                <span className="sidebar-badge" data-testid={`fc-sidebar-v2-db-count-${d.id}`}>
                  {d.count.toLocaleString('en-US')}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* F 471 — the separate "HQ" group is GONE. HQ Backup is a row in the
             Databases list above, like every other database, and it routes
             through the SAME `onPageSelect(id, 'database')` seam those rows
             use. The component that drew it (`HqBackupDatabase`) was a second
             implementation of the database row: it read the count from a
             separate manifest and loaded the items on click. Now that the
             items live INSIDE the record (F 470), the count comes from the
             record the list already has, and the row needs nothing of its
             own. One component, one list. ---- */}

        {/* ---- Page tree: the existing component, mounted, not copied ----
             F 472 — the tree's own "New page" and "New task" buttons are GONE.
             They rendered INSIDE this group, under the "Pages" heading, which
             is why a TASK button appeared to belong to Pages. The section's
             "+" replaces both, and the tree publishes its create action up so
             the button can drive create + select + inline rename. */}
        <div className="sidebar-group" data-testid="fc-sidebar-v2-pages">
          <div className="sidebar-group__header">
            <SectionToggle
              section="pages"
              open={isSectionOpen('pages')}
              onToggle={() => toggleSection('pages')}
            >
              <h3 className="sidebar-group__heading">Pages</h3>
            </SectionToggle>
            <SectionAction
              label="New page"
              testId="fc-sidebar-v2-new-page"
              onAction={handleCreatePage}
            />
          </div>
          <div
            id="fc-sidebar-v2-pages-content"
            hidden={!isSectionOpen('pages')}
          >
            <PageTree
              phase={phase}
              currentPageId={currentPageId}
              onPageSelect={onPageSelect}
              onRegisterCreate={registerCreatePage}
            />
          </div>
        </div>

        {/* F 226 — TRASH. Last group in the sidebar, below PAGES, per
            the dispatch. It is a button rather than a page row because a
            trashed page must not be selectable like a live one. */}
        <div className="sidebar-group" data-testid="fc-sidebar-v2-trash">
          {/* F 229 — every other group in this sidebar opens with a
              `sidebar-group__heading`. Trash was the only one that did
              not, which is why it read as an orphan rather than as a
              section. Same class, so the same small-caps/muted/uppercase
              treatment applies with no new rule. */}
          <div className="sidebar-group__header">
            <h3 className="sidebar-group__heading">Trash</h3>
          </div>
          <button
            type="button"
            className="sidebar-item"
            data-testid="fc-sidebar-v2-trash-button"
            data-current={trashOpen}
            onClick={() => onTrashToggle?.()}
            title="Trash"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              width: '100%', padding: '4px 8px', border: 'none',
              borderRadius: 4, cursor: 'pointer', textAlign: 'left',
              // F 368 — `--fc-bg-active` is a token no theme writes (see
              // design-system.css), and the `#e8e8e8` fallback baked in here
              // is a LIGHT grey: if the token ever went missing, this open row
              // would paint a near-white band in dark mode under light text.
              // `--fc-bg-tertiary` is written by the theme system in both modes.
              background: trashOpen ? 'var(--fc-bg-tertiary)' : 'transparent',
              color: 'var(--fc-text-secondary, inherit)',
            }}
          >
            <span className="sidebar-item__icon" aria-hidden="true">🗑️</span>
            <span className="sidebar-label">Trash</span>
          </button>
        </div>
      </Stack>
      <NewDatabaseModal
        opened={newDbOpen}
        onClose={() => setNewDbOpen(false)}
        onCreate={handleCreateDatabaseSubmit}
      />
    </Box>
  );
};

export default SidebarV2;
